/*
 * Display-side mirror of the backend's step scoring constants. The backend computes and stores
 * every score; these only reconstruct the "passes without review" and "stays with people" splits
 * the step tile explains, from the stored labels.
 */

/** Share of coordination effort that stays with people; the rest is addressable. */
export const COORDINATION_HUMAN_SHARE = 0.5;

/** Share of effort a human keeps for verification, by error-cost rating. Interpolated linearly between anchors. */
export const VERIFICATION_RETENTION_ANCHORS: readonly (readonly [rating: number, retention: number])[] = [
    [1, 0.5],
    [3, 0.35],
    [5, 0.2],
    [7, 0.1],
    [10, 0],
];

/** Retention floor when a human is mandated to perform or decide the step. */
export const MANDATED_HUMAN_RETENTION = 0.3;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const interpolateRetention = (errorCost: number): number => {
    const anchors = VERIFICATION_RETENTION_ANCHORS;
    const first = anchors[0];
    const last = anchors.at(-1);

    if (first === undefined || last === undefined) return 0;
    if (errorCost <= first[0]) return first[1];
    if (errorCost >= last[0]) return last[1];

    const upperIndex = anchors.findIndex(([rating]) => errorCost <= rating);
    const upper = anchors[upperIndex];
    const lower = anchors[upperIndex - 1];

    if (upper === undefined || lower === undefined) return last[1];

    const progress = (errorCost - lower[0]) / (upper[0] - lower[0]);

    return lower[1] + (upper[1] - lower[1]) * progress;
};

export const verificationRetention = (errorCost: number, mandatedHumanInvolvement: boolean): number =>
    clamp01(Math.max(interpolateRetention(errorCost), mandatedHumanInvolvement ? MANDATED_HUMAN_RETENTION : 0));

export const toPercent = (fraction: number): number => Math.round(fraction * 100);
